import { ContactPerson, CurdDataSetConfig, FormFieldType, Property, PropertyConfig } from '../types';
import { toDateFormat } from '@mri/functions';
import { ChoiceTypes } from '@mri/enums';
import { tap } from 'rxjs';
import { propertyCommonField } from './property-common-field';

const filterConfig = [
  { key: 'name', label: 'Nombre', type: FormFieldType.STRING },
  {
    key: 'status',
    label: 'Estado',
    type: FormFieldType.OPTIONS_MULTIPLE,
    options: [
      { label: 'Activado', value: 'active'},
      { label: 'Desactivado', value: 'disable'}
    ]
  }
];
export const propertyCurdDataSetConfig: CurdDataSetConfig<Property> = {
  entityName: 'Propietario',
  collectionName: 'Properties',
  gatewayUrl: 'properties',
  baseBreadcrumb: 'properties',
  form: {
    listRedirectUrl: 'console/properties/list',
    fields: [
      {
        key: 'name',
        label: 'Nombre de la Propiedad',
        type: FormFieldType.STRING,
        containerClass: 'col-12'
      },
      {
        key: 'profileIds',
        label: 'Licencia',
        type: FormFieldType.OPTIONS_MULTIPLE,
        containerClass: 'col-md-6 col-sm-6',
        optionsGateway: 'profiles',
        filterOptions: { type: 'licence' }
      },
      {
        key: 'timezone',
        label: 'Zona Horaria',
        type: FormFieldType.OPTIONS,
        containerClass: 'col-md-6 col-sm-6',
        optionsConfigKey: ChoiceTypes.TIMEZONE,
        fillValue: p => p?.config?.timezone || ''
      },
      {
        key: 'firstName',
        label: 'Nombre del propietario',
        type: FormFieldType.STRING,
        containerClass: 'col-md-6 col-sm-6',
        optionsConfigKey: ChoiceTypes.MOBILE_ICON,
        fillValue: p => p?.agent?.firstName || ''
      },
      {
        key: 'lastName',
        label: 'Apellido del propietario',
        type: FormFieldType.STRING,
        containerClass: 'col-md-6 col-sm-6',
        fillValue: p => p?.agent?.lastName || ''
      },
      {
        key: 'email',
        label: 'Email del propietario',
        type: FormFieldType.STRING,
        fillValue: p => p?.agent?.email || '',
        containerClass: 'col-12'
      },
      {
        key: 'phone',
        label: 'Telefono del propietario',
        type: FormFieldType.STRING,
        containerClass: 'col-12',
        fillValue: p => p?.agent?.phone || ''
      },
      {
        key: 'distanceUnit',
        label: 'Unidad de distancia',
        type: FormFieldType.OPTIONS,
        containerClass: 'col-md-6 col-sm-6',
        optionsConfigKey: ChoiceTypes.DISTANCE,
        fillValue: p => p?.config?.distanceUnit || ''
      },
      {
        key: 'speedUnit',
        label: 'Unidad de velocidad',
        type: FormFieldType.OPTIONS,
        containerClass: 'col-md-6 col-sm-6',
        optionsConfigKey: ChoiceTypes.SPEED,
        fillValue: p => p?.config?.speedUnit || ''
      },
      propertyCommonField
    ],
    handleObject: data => {
      const {
        timezone,
        distanceUnit,
        speedUnit, firstName, lastName,
        phone, email, name, profileIds
      } = data as unknown as ContactPerson & PropertyConfig & { name: string, profileIds: string[] };

      return {
        name,
        agent: {
          phone, email, firstName, lastName
        },
        config: {
          speedUnit, distanceUnit, timezone
        },
        profileIds
      } as Property;
    }
  },
  list: {
    pageConfig: {
      title: 'Lista de propietarios',
      breadcrumb: [
        {
          title: 'Propietarios',
          type: 'item',
          link: '/console/properties/list'
        },
        {
          title: 'Lista',
          type: 'item',
          link: '/console/properties/list',
          pageName: 'Propietarios'
        }
      ]
    },
    table: {
      columns: [
        {
          key: 'name', label: 'Nombre',
          getValue({ element, component }) {
            if (!component) return element.name;

            const auth = component.authSrv.auth;
            const meIndicator = auth?.property?._id === element._id ? '<span class="badge text-success-500" >Actual</span>' : '';

            const ownIndicator = auth?.property?._id === element?.propertyId ? '<span class="badge text-primary-500" >Tuyo</span>' : '';

            return `${element.name} ${meIndicator} ${ownIndicator}`;
          }
        },
        {
          key: 'type', label: 'Permisos',
          getValue: ({ element }) => {
            const permissions =
              [...new Set(element.profiles?.map(p => p.permissions).flat())];

            if (permissions.length === 0) return 'No tiene permisos';

            const [p1, p2, ...restP] = permissions;
            return [p1, p2]
              .filter(p => !!p)
              .map(p => `<span class="badge">${p?.key}</span>`)
              .join('') + (restP.length > 0 ? `<span class="badge">${restP.length}+</span>` : '');
          }
        },
        { key: 'mobileCount', label: 'Vehiculos', getValue: ({ element }) => element.mobiles?.length?.toString() || '0' },

        { key: 'owner', label: 'Dueño', getValue: ({ element }) => element?.own?.name || 'Nadie' },
        { key: 'status', label: 'Estado', getValue: ({ element }) => element.disable ? 'Inactivo' : 'Activo' },
        { key: 'createdTime', label: 'Fecha de creación', getValue: ({ element }) => toDateFormat(element.createdTime) }
      ]
    },
    actionsKeys: ['create', 'edit', 'transfer', 'delete'],
    filter: filterConfig,
    searchFilter: filterConfig,
    extraActions: [
      {
        key: 'into',
        label: 'Ingresar',
        icon: 'fi fi-rr-exit-alt',
        inRow: true,
        click: ({ selectedElements, component }) => {
          if (!component) return;
          const [element] = selectedElements || [];
          if (!element) return;

          const authRsrSrv = component.authSrv;

          return authRsrSrv.selectProperty(element._id)
            .pipe(
              tap(() => window.location.reload())
            );
        },
        disableWhen: ({ selectedElements, component }) => {
          if (!component) return selectedElements?.length !== 1;

          const auth = component.authSrv.auth;
          return selectedElements?.length !== 1 || auth?.property?._id === selectedElements[0]._id;
        }
      },
      {
        key: 'personalize',
        label: 'Personalizar',
        icon: 'fi fi-rr-settings',
        link: ({ selectedElements }) => selectedElements?.[0]?._id ? ['..', selectedElements?.[0]?._id, 'personalize'] : null,

        disableWhen: ({ selectedElements }) => {
          return selectedElements?.length !== 1;
        }
      }
    ]
  },
  pages: ['list', 'create', 'edit']
};
